import React, { useState, useRef } from 'react';
import { rotateImage, flipImage } from './rotatorUtils';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import './Rotator.css';

const Rotator = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [processedImage, setProcessedImage] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [isFlippedHorizontal, setIsFlippedHorizontal] = useState(false);
  const [isFlippedVertical, setIsFlippedVertical] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const canvasRef = useRef(null);

  const handleImageSelected = (img) => {
    setOriginalImage(img);
    setProcessedImage(null);
    setRotation(0);
    setIsFlippedHorizontal(false);
    setIsFlippedVertical(false);
  };

  const handleRotateLeft = () => {
    setRotation((prev) => (prev - 90) % 360);
    applyTransformations();
  };

  const handleRotateRight = () => {
    setRotation((prev) => (prev + 90) % 360);
    applyTransformations();
  };

  const handleFlipHorizontal = () => {
    setIsFlippedHorizontal((prev) => !prev);
    applyTransformations();
  };

  const handleFlipVertical = () => {
    setIsFlippedVertical((prev) => !prev);
    applyTransformations();
  };

  const applyTransformations = async () => {
    if (!originalImage) return;
    
    setIsProcessing(true);
    try {
      // First apply rotation
      const rotated = await rotateImage(originalImage, rotation, canvasRef.current);
      
      // Then apply flipping
      const flipped = await flipImage(
        rotated, 
        isFlippedHorizontal, 
        isFlippedVertical, 
        canvasRef.current
      );
      
      setProcessedImage(flipped);
    } catch (error) {
      console.error('Error processing image:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!processedImage) return;
    
    const link = document.createElement('a');
    link.href = processedImage;
    link.download = 'rotated-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="rotator tool-container">
      <div className="container">
        <div className="tool-header">
          <h1>Image Rotator</h1>
          <p>Rotate and flip your images</p>
        </div>
        
        <div className="tool-content">
          <ImageUploader 
            onImageSelected={handleImageSelected}
            selectedImage={originalImage}
          />
          
          {originalImage && (
            <div className="rotator-workspace">
              <div className="rotation-controls">
                <div className="control-buttons">
                  <button 
                    className="btn btn-secondary rotate-btn" 
                    onClick={handleRotateLeft}
                    disabled={isProcessing}
                  >
                    <span className="rotate-icon rotate-left">↺</span> Rotate Left
                  </button>
                  <button 
                    className="btn btn-secondary rotate-btn" 
                    onClick={handleRotateRight}
                    disabled={isProcessing}
                  >
                    <span className="rotate-icon rotate-right">↻</span> Rotate Right
                  </button>
                </div>
                
                <div className="flip-controls">
                  <button 
                    className={`btn ${isFlippedHorizontal ? 'btn-primary' : 'btn-secondary'} flip-btn`} 
                    onClick={handleFlipHorizontal}
                    disabled={isProcessing}
                  >
                    <span className="flip-icon">↔</span> Flip Horizontal
                  </button>
                  <button 
                    className={`btn ${isFlippedVertical ? 'btn-primary' : 'btn-secondary'} flip-btn`}
                    onClick={handleFlipVertical}
                    disabled={isProcessing}
                  >
                    <span className="flip-icon">↕</span> Flip Vertical
                  </button>
                </div>
                
                <div className="rotation-info">
                  <p>Current Rotation: {rotation}°</p>
                  <p>Flipped: {isFlippedHorizontal || isFlippedVertical ? 'Yes' : 'No'}</p>
                </div>
              </div>
              
              <div className="image-preview-section">
                <div className="image-preview-container">
                  <h3>Original</h3>
                  <div className="image-preview">
                    <img src={originalImage.src} alt="Original" />
                  </div>
                </div>
                
                {processedImage && (
                  <div className="image-preview-container">
                    <h3>Transformed</h3>
                    <div className="image-preview">
                      <img src={processedImage} alt="Transformed" />
                    </div>
                    <button 
                      className="btn btn-primary download-btn" 
                      onClick={handleDownload}
                    >
                      Download Image
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Hidden canvas for image processing */}
          <canvas ref={canvasRef} style={{ display: 'none' }}></canvas>
        </div>
      </div>
    </div>
  );
};

export default Rotator; 